import {
  Button,
  DialogActions as MuiDialogActions,
  Typography,
} from '@global-engineering-shared/gweb-material-ui';
import { useEffect } from 'react';
import { Link } from 'react-router';

import Dialog from 'components/Dialog';
import { useDialog } from 'context/DialogProvider/DialogProvider';
import { useRefresh } from 'context/RefreshProvider/RefreshProvider';
import { useSelectedEntitiesContext } from 'context/SelectedEntities/SelectedEntitiesProvider';
import useStyles from 'features/Entities/components/EntitiesPageResultDialog/EntitiesPageResultDialog.styles';
import { IActionResponse } from 'features/Entities/types/ActionResponse';
import { DRAFTS_PAGE_URL, ENTITIES_PAGE_RESULT_DIALOG_ID } from 'utils/defaults';

import { getDraftsDialogMessage, getNoDraftsDialogMessage } from './utils/resultDialogMessages';

export interface IEntitiesPageResultDialogProps {
  actionResponse: IActionResponse;
}

const EntitiesPageResultDialog = ({ actionResponse }: IEntitiesPageResultDialogProps) => {
  const { totalItemsActioned, spaceIdsWithDraft, spaceIdsWithoutDraft } = actionResponse;
  const { isDialogOpen, closeDialog } = useDialog();
  const dialogId = ENTITIES_PAGE_RESULT_DIALOG_ID;
  const url = `${DRAFTS_PAGE_URL}?spaceIds=${spaceIdsWithDraft.join('%2C')}`;
  const classes = useStyles();

  const { resetSelectedEntities } = useSelectedEntitiesContext();
  const { triggerRefresh } = useRefresh();
  const isOpen = isDialogOpen(dialogId);

  useEffect(() => {
    if (isOpen) {
      resetSelectedEntities();
      triggerRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const hasIdsWithDrafts = !!spaceIdsWithDraft.length;
  const hasIdsWithoutDrafts = !!spaceIdsWithoutDraft.length;
  const draftsCount = spaceIdsWithDraft.length;

  const noDraftsMessage = getNoDraftsDialogMessage(spaceIdsWithoutDraft, classes);
  const draftsMessage = getDraftsDialogMessage(spaceIdsWithDraft);

  return (
    <Dialog
      isOpen={isDialogOpen(dialogId)}
      handleClose={() => {
        closeDialog(dialogId);
      }}
      handleContinue={() => {
        closeDialog(dialogId);
      }}
      showContinueButton={!hasIdsWithDrafts}
      name="EntitiesPageResultDialog"
      title="Actioning Result"
      content={
        <>
          <Typography color="textPrimary" variant="body2" className={classes.text}>
            You have actioned {totalItemsActioned}{' '}
            {totalItemsActioned === 1 ? 'entity' : 'entities'}.{' '}
          </Typography>
          {hasIdsWithoutDrafts && noDraftsMessage}
          {hasIdsWithDrafts && draftsMessage}
          {hasIdsWithDrafts && (
            <MuiDialogActions>
              <Link to={url}>
                <Button onClick={() => closeDialog(dialogId)} color="primary" variant="contained">
                  See {draftsCount === 1 ? 'Draft' : 'Drafts'}
                </Button>
              </Link>
            </MuiDialogActions>
          )}
        </>
      }
    />
  );
};

export default EntitiesPageResultDialog;
